import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, finalize } from 'rxjs';
import { BedStatesService } from '../../../core/services/bed-states.service';
import { BedPosture, IBedState, IBedStateHistory } from '../../models/be/bed-state';
import { BedPostureIconComponent, formatBedDuration, posturePresentation } from './bed-posture-icon.component';

@Component({
  selector: 'app-bed-state-history-dialog',
  imports: [FormsModule, MatDialogModule, TranslateModule, BedPostureIconComponent],
  templateUrl: './bed-state-history-dialog.component.html',
  styleUrl: './bed-state-history-dialog.component.scss'
})
export class BedStateHistoryDialogComponent implements OnInit, OnDestroy {
  readonly bed = inject<IBedState>(MAT_DIALOG_DATA);
  readonly store = inject(BedStatesService);
  private readonly translate = inject(TranslateService);
  readonly presentation = posturePresentation;
  readonly duration = formatBedDuration;
  readonly inputTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  readonly summaryKeys: { key: keyof IBedStateHistory['summarySeconds']; state: BedPosture | null }[] = [
    { key: 'lying', state: 'LYING_IN_BED' }, { key: 'sitting', state: 'SITTING_ON_BED' },
    { key: 'outOfBed', state: 'OUT_OF_BED' }, { key: 'unknown', state: null }
  ];
  fromInput = '';
  toInput = '';
  history: IBedStateHistory | null = null;
  loading = false;
  error = '';
  visibleCount = 50;
  private request?: Subscription;
  private invalidation?: Subscription;
  private release?: () => void;

  ngOnInit(): void {
    if (!this.store.canView) return;
    this.release = this.store.acquire();
    this.invalidation = this.store.invalidated$.subscribe(() => {
      this.request?.unsubscribe();
      this.history = null;
      this.error = 'BED_STATES.FORBIDDEN';
    });
    this.selectPeriod(24);
  }
  ngOnDestroy(): void { this.request?.unsubscribe(); this.invalidation?.unsubscribe(); this.release?.(); }

  selectPeriod(hours: number): void {
    const end = Date.now();
    this.fromInput = this.localInput(new Date(end - hours * 3600000));
    this.toInput = this.localInput(new Date(end));
    this.load();
  }

  load(): void {
    this.request?.unsubscribe();
    this.history = null;
    this.error = '';
    this.visibleCount = 50;
    if (!this.store.canView || this.store.denied()) { this.error = 'BED_STATES.FORBIDDEN'; return; }
    const from = new Date(this.fromInput), to = new Date(this.toInput);
    if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || from >= to || from.getTime() >= Date.now()) {
      this.error = 'BED_STATES.INVALID_RANGE'; return;
    }
    this.loading = true;
    this.request = this.store.history$(this.bed, from.toISOString(), to.toISOString()).pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      next: history => {
        if (history.deviceId !== this.bed.deviceId || history.logicalBedId !== this.bed.logicalBedId) {
          this.error = 'BED_STATES.HISTORY_ERROR'; return;
        }
        this.history = history;
      },
      error: (error: unknown) => {
        const status = error instanceof HttpErrorResponse ? error.status : 0;
        this.error = status === 422 ? 'BED_STATES.TOO_MANY_EVENTS'
          : status === 400 ? 'BED_STATES.INVALID_RANGE'
          : status === 404 ? 'BED_STATES.SENSOR_NOT_FOUND'
          : [401, 403].includes(status) ? 'BED_STATES.FORBIDDEN' : 'BED_STATES.HISTORY_ERROR';
      }
    });
  }

  intervalDuration(interval: IBedStateHistory['intervals'][number]): string {
    return formatBedDuration((Date.parse(interval.end ?? this.history!.to) - Date.parse(interval.start)) / 1000);
  }

  formatDate(value: string): string {
    const locale = this.translate.currentLang === 'en' ? 'en-GB' : 'it-IT';
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short', timeZone: this.history?.timezone || 'UTC' };
    return new Intl.DateTimeFormat(locale, options).format(new Date(value));
  }

  private localInput(value: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
  }
}
